import { useState, useEffect } from "react";
import { AdminLayout } from "@/project/jain_elibrary-admin/components/layout/AdminLayout";
import { Button } from "@/project/jain_elibrary-admin/components/ui/button";
import { Input } from "@/project/jain_elibrary-admin/components/ui/input";
import { Label } from "@/project/jain_elibrary-admin/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/project/jain_elibrary-admin/components/ui/select";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/project/jain_elibrary-admin/components/ui/card";
import { Checkbox } from "@/project/jain_elibrary-admin/components/ui/checkbox";
import {
  Download,
  Loader2,
  FileText,
  FileSpreadsheet,
  FileJson,
  Filter,
  Search,
  Eye,
  FileText as FileTextIcon,
} from "lucide-react";
import { adminService, Statistics } from "../../services/adminService";
import { getPreviewCountMatchingStatistics } from "./exportPreviewStats";
import { toast } from "sonner";
import { showErrorToast } from "@/utils/errorHandler";

type ExportFormat = "csv" | "excel" | "json";
type ContentMode = "metadata" | "full";

interface AgamFilters {
  language?: string;
  search?: string;
}

interface ExportProgress {
  currentPage: number;
  totalPages: number;
  fetchedRecords: number;
  status: string;
}

// Available Agam fields for export
const AGAM_FIELDS = [
  { key: "all_serial", label: "Serial No" },
  { key: "scripture_name", label: "Scripture Name" },
  { key: "mool_scripture_name", label: "Mool Scripture Name" },
  { key: "trans_scripture_name", label: "Trans Scripture Name" },
  { key: "author", label: "Author" },
  { key: "original_author", label: "Original Author" },
  { key: "mool_language", label: "Mool Language" },
  { key: "trans_language", label: "Trans Language" },
  { key: "category", label: "Category" },
  { key: "category1", label: "Category 1" },
  { key: "category2", label: "Category 2" },
  { key: "category3", label: "Category 3" },
  { key: "chapter", label: "Chapter" },
  { key: "section", label: "Section" },
  { key: "gatha_or_sutra", label: "Gatha or Sutra" },
  { key: "sutra_num", label: "Sutra Number" },
  { key: "mool_sutra", label: "Mool Sutra" },
  { key: "trans_sutra", label: "Trans Sutra" },
  { key: "sutra_anuyog", label: "Sutra Anuyog" },
  { key: "century", label: "Century" },
  { key: "sect", label: "Sect" },
  { key: "source", label: "Source" },
];

// Download CSV function
const downloadCSVFile = (csvData: string, filename: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    try {
      if (!csvData || csvData.length === 0) {
        reject(new Error("CSV data is empty"));
        return;
      }

      const BOM = "\uFEFF";
      const csvWithBOM = BOM + csvData;
      const blob = new Blob([csvWithBOM], {
        type: "text/csv;charset=utf-8;",
      });

      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.download = `${filename}.csv`;
      link.style.display = "none";
      document.body.appendChild(link);

      requestAnimationFrame(() => {
        try {
          link.click();
          setTimeout(() => {
            if (document.body.contains(link)) {
              document.body.removeChild(link);
            }
            URL.revokeObjectURL(url);
            resolve();
          }, 200);
        } catch (clickError) {
          if (document.body.contains(link)) {
            document.body.removeChild(link);
          }
          URL.revokeObjectURL(url);
          reject(clickError);
        }
      });
    } catch (error) {
      reject(error);
    }
  });
};

// Download JSON function
const downloadJSONFile = (jsonData: any, filename: string): void => {
  const jsonString = JSON.stringify(jsonData, null, 2);
  const blob = new Blob([jsonString], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${filename}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Download Excel (CSV with .xlsx extension - simplified)
const downloadExcelFile = (
  csvData: string,
  filename: string
): Promise<void> => {
  return downloadCSVFile(csvData, filename.replace(".xlsx", ""));
};

const ExportAgam = () => {
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [loading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [filters, setFilters] = useState<AgamFilters>({
    language: "",
    search: "",
  });
  const [exportFormat, setExportFormat] = useState<ExportFormat>("csv");
  const [contentMode] = useState<ContentMode>("metadata");
  const [selectedFields, setSelectedFields] = useState<Set<string>>(
    new Set([
      "all_serial",
      "scripture_name",
      "mool_scripture_name",
      "trans_scripture_name",
      "author",
      "mool_language",
      "trans_language",
      "category",
    ])
  );
  const [exportProgress, setExportProgress] = useState<ExportProgress>({
    currentPage: 0,
    totalPages: 0,
    fetchedRecords: 0,
    status: "",
  });
  const [previewResults, setPreviewResults] = useState<any[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewTotal, setPreviewTotal] = useState(0);
  const [showPreview, setShowPreview] = useState(false);

  // Options for filters
  const [authors, setAuthors] = useState<any[]>([]);
  const [publishers, setPublishers] = useState<any[]>([]);
  // Hardcoded list of 11 languages available in the system
  const languages = [
    { name: "Apabhramsha" },
    { name: "Braj" },
    { name: "Devnagari" },
    { name: "Dhundari" },
    { name: "English" },
    { name: "Germany" },
    { name: "Gujarati" },
    { name: "Hindi" },
    { name: "Marathi" },
    { name: "Prakrita" },
    { name: "Sanskrit" },
  ];
  const [_loadingOptions, setLoadingOptions] = useState(false);

  useEffect(() => {
    fetchFilterOptions();
    adminService
      .getStatistics()
      .then(setStatistics)
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helper function to fetch all records with pagination
  const fetchAllRecords = async (
    fetchFn: (params: any) => Promise<any>,
    pageSize: number = 100,
    recordType: string = "records"
  ): Promise<any[]> => {
    const allRecords: any[] = [];
    let page = 1;
    let hasMore = true;
    const maxPages = 100; // Safety limit
    let pagesFetched = 0;

    console.log(`Starting to fetch ${recordType}...`);

    while (hasMore && pagesFetched < maxPages) {
      try {
        console.log(
          `Fetching ${recordType} - page ${page}, page_size ${pageSize}`
        );
        const response = await fetchFn({ page, page_size: pageSize });
        console.log(`${recordType} response:`, response);

        // Handle different response formats
        let results: any[] = [];
        if (Array.isArray(response)) {
          results = response;
          hasMore = false; // If it's an array, assume it's all the data
        } else if (response && Array.isArray(response.results)) {
          results = response.results;
        } else if (response?.data && Array.isArray(response.data.results)) {
          results = response.data.results;
        } else if (response?.data && Array.isArray(response.data)) {
          results = response.data;
        }

        console.log(
          `${recordType} - extracted ${results.length} results from page ${page}`
        );

        if (results.length > 0) {
          allRecords.push(...results);
        }

        // Check if there's more data
        const totalCount = response?.count ?? 0;
        const hasNext = response?.next ?? false;

        if (hasNext && results.length === pageSize) {
          // There's a next page and we got a full page of results
          page++;
          pagesFetched++;
        } else if (totalCount > 0 && allRecords.length >= totalCount) {
          // We've fetched all records
          hasMore = false;
        } else if (results.length < pageSize) {
          // Got less than pageSize, so this is the last page
          hasMore = false;
        } else {
          // No more data
          hasMore = false;
        }
      } catch (error) {
        console.error(`Error fetching ${recordType} on page ${page}:`, error);
        hasMore = false;
      }
    }

    console.log(
      `Finished fetching ${recordType}. Total records: ${allRecords.length}`
    );
    return allRecords;
  };

  const fetchFilterOptions = async () => {
    // Immediately enable dropdowns - they can work with empty options initially
    setLoadingOptions(false);

    try {
      console.log("Starting to fetch filter options...");

      // Fetch all authors and publishers in parallel
      const [allAuthors, allPublishers] = await Promise.all([
        fetchAllRecords(
          params => adminService.getAuthors(params),
          100,
          "authors"
        ).catch(err => {
          console.error("Error fetching authors:", err);
          return [];
        }),
        fetchAllRecords(
          params => adminService.getPublishers(params),
          100,
          "publishers"
        ).catch(err => {
          console.error("Error fetching publishers:", err);
          return [];
        }),
      ]);

      console.log("Filter options fetched:", {
        authors: allAuthors.length,
        publishers: allPublishers.length,
      });

      setAuthors(Array.isArray(allAuthors) ? allAuthors : []);
      setPublishers(Array.isArray(allPublishers) ? allPublishers : []);

      console.log(
        `State updated - Authors: ${authors.length}, Publishers: ${publishers.length}`
      );
    } catch (error: any) {
      showErrorToast(error, "Failed to load filter options");
      // Ensure we set empty arrays on error
      setAuthors([]);
      setPublishers([]);
    }
  };

  // Fetch all Agams with pagination
  const fetchAllAgams = async (params: any = {}): Promise<any[]> => {
    const allAgams: any[] = [];
    let page = 1;
    const pageSize = 100;
    let hasMore = true;
    let totalCount = 0;
    let estimatedPages = 1;

    while (hasMore) {
      try {
        setExportProgress({
          currentPage: page,
          totalPages: estimatedPages,
          fetchedRecords: allAgams.length,
          status: `Fetching Agams page ${page}...`,
        });

        // Only pass supported parameters: page, page_size, search, ordering
        const AgamParams: any = {
          page,
          page_size: pageSize,
        };
        if (params.search) {
          AgamParams.search = params.search;
        }
        if (params.language) {
          AgamParams.language = params.language;
        }
        if (params.ordering) {
          AgamParams.ordering = params.ordering;
        }

        console.log(
          `ExportAgam - Fetching page ${page} with params:`,
          AgamParams
        );
        const response = await adminService.getAgam(AgamParams);
        console.log(
          `ExportAgam - Page ${page} response: count=${response.count}, results=${response.results?.length}`
        );

        if (page === 1 && response.count) {
          totalCount = response.count;
          estimatedPages = Math.ceil(totalCount / pageSize);
        }

        allAgams.push(...response.results);

        if (!response.next) {
          hasMore = false;
        } else {
          page++;
        }
      } catch (error: any) {
        console.error(`Error fetching Agams page ${page}:`, error);
        showErrorToast(error, `Failed to fetch Agams page ${page}`);
        hasMore = false;
      }
    }

    return allAgams;
  };

  // Convert Agams to CSV
  const convertToCSV = (Agams: any[]): string => {
    if (Agams.length === 0) {
      return "";
    }

    // Use selected fields or default to all available fields
    const headers = Array.from(selectedFields).filter(key =>
      AGAM_FIELDS.some(field => field.key === key)
    );

    if (headers.length === 0) {
      // If no fields selected, use default fields
      headers.push(
        "all_serial",
        "scripture_name",
        "mool_scripture_name",
        "trans_scripture_name",
        "author",
        "mool_language",
        "trans_language",
        "category"
      );
    }

    // Create CSV rows
    const rows = Agams.map(Agam => {
      return headers.map(header => {
        // Handle field name variations for Agams
        // Agam API returns: title/title_en (not title_english), publisher_name (not publisher), authors array (not author)
        let value = Agam[header];

        // Agam API uses different field names than books/magazines
        // No special mapping needed - Agam fields match the model directly

        // For metadata mode, exclude full content fields
        if (contentMode === "metadata") {
          // Skip description/comment if they're too long (full content)
          if (
            (header === "description" || header === "comment") &&
            value &&
            value.length > 500
          ) {
            value = value.substring(0, 500) + "...";
          }
        }

        const stringValue =
          value !== null && value !== undefined ? String(value) : "";
        if (
          stringValue.includes(",") ||
          stringValue.includes('"') ||
          stringValue.includes("\n")
        ) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      });
    });

    return [headers.join(","), ...rows.map(row => row.join(","))].join("\n");
  };

  // Preview filtered results
  const handlePreview = async () => {
    try {
      setPreviewLoading(true);
      setShowPreview(false);
      toast.info("Loading filtered results...");

      const params: any = {
        page: 1,
        page_size: 100, // Show first 100 results in preview
      };

      // Add language filter if provided
      if (filters.language && filters.language !== "all") {
        params.language = filters.language.trim();
      }

      // Add search filter if provided
      if (filters.search?.trim()) {
        params.search = filters.search.trim();
      }

      console.log("ExportAgam - Preview params:", params);
      const response = await adminService.getAgam(params);
      console.log("ExportAgam - Preview response count:", response.count);
      console.log(
        "ExportAgam - Preview response results length:",
        response.results?.length
      );

      // Log first record to see what fields are available
      if (response.results && response.results.length > 0) {
        const firstRecord = response.results[0];
        console.log(
          "ExportAgam - First record fields:",
          Object.keys(firstRecord)
        );
        console.log("ExportAgam - First record sample:", firstRecord);

        // Specifically check for author-related fields
        const authorFields = Object.keys(firstRecord).filter(
          key =>
            key.toLowerCase().includes("author") ||
            key.toLowerCase().includes("writer") ||
            key.toLowerCase().includes("creator")
        );
        console.log("ExportAgam - Author-related fields found:", authorFields);
        if (authorFields.length > 0) {
          authorFields.forEach(field => {
            console.log(`ExportAgam - ${field}:`, firstRecord[field]);
          });
        }
      }

      const hasListFilters =
        Boolean(filters.search?.trim()) ||
        Boolean(
          filters.language &&
            filters.language !== "all" &&
            filters.language.trim()
        );

      const previewCount = getPreviewCountMatchingStatistics(
        "agam",
        statistics,
        response.count ?? 0,
        hasListFilters
      );

      setPreviewResults(response.results ?? []);
      setPreviewTotal(previewCount);
      setShowPreview(true);

      if (previewCount === 0) {
        toast.warning("No Agams found matching your filters");
      } else {
        toast.success(
          `Found ${previewCount.toLocaleString()} Agam(s). Showing first ${response.results.length} in preview.`
        );
      }
    } catch (error: any) {
      console.error("Error previewing results:", error);
      showErrorToast(error, "Failed to load preview. Please try again.");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      setExportProgress({
        currentPage: 0,
        totalPages: 0,
        fetchedRecords: 0,
        status: "Starting export...",
      });

      toast.info("Fetching Agams data... This may take a few minutes.");

      // Build filter params
      const params: any = {};

      // Add language filter if provided
      if (filters.language && filters.language !== "all") {
        params.language = filters.language.trim();
      }

      // Add search filter if provided
      if (filters.search?.trim()) {
        params.search = filters.search.trim();
      }

      console.log("ExportAgam - Export params:", params);
      // Fetch all Agams - only Agams, nothing else
      const allAgams = await fetchAllAgams(params);
      console.log("ExportAgam - Total Agams fetched:", allAgams.length);

      if (allAgams.length === 0) {
        toast.warning("No Agams found to export");
        setExporting(false);
        return;
      }

      // Filter content based on mode
      let AgamsToExport = allAgams;
      if (contentMode === "metadata") {
        // Remove or truncate full content fields
        AgamsToExport = allAgams.map(Agam => {
          const { description, comment, ...metadata } = Agam;
          return {
            ...metadata,
            description:
              description && description.length > 500
                ? description.substring(0, 500) + "..."
                : description,
            comment:
              comment && comment.length > 500
                ? comment.substring(0, 500) + "..."
                : comment,
          };
        });
      }

      setExportProgress({
        currentPage: 0,
        totalPages: 0,
        fetchedRecords: AgamsToExport.length,
        status: "Converting to export format...",
      });

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().split("T")[0];
      const filename = `agams_export_${timestamp}_${contentMode}`;

      // Export based on format
      if (exportFormat === "json") {
        downloadJSONFile(AgamsToExport, filename);
        toast.success(
          `Successfully exported ${AgamsToExport.length} Agam(s) to JSON`
        );
      } else if (exportFormat === "csv" || exportFormat === "excel") {
        const csvData = convertToCSV(AgamsToExport);
        if (exportFormat === "excel") {
          await downloadExcelFile(csvData, filename);
        } else {
          await downloadCSVFile(csvData, filename);
        }
        toast.success(
          `Successfully exported ${AgamsToExport.length} Agam(s) to ${exportFormat.toUpperCase()}`
        );
      }

      setExportProgress({
        currentPage: 0,
        totalPages: 0,
        fetchedRecords: AgamsToExport.length,
        status: "Export completed!",
      });
    } catch (error: any) {
      console.error("Error exporting Agams:", error);
      showErrorToast(error, "Failed to export Agams");
    } finally {
      setExporting(false);
    }
  };

  const toggleField = (fieldKey: string) => {
    const newFields = new Set(selectedFields);
    if (newFields.has(fieldKey)) {
      newFields.delete(fieldKey);
    } else {
      newFields.add(fieldKey);
    }
    setSelectedFields(newFields);
  };

  const selectAllFields = () => {
    setSelectedFields(new Set(AGAM_FIELDS.map(f => f.key)));
  };

  const deselectAllFields = () => {
    setSelectedFields(new Set());
  };

  const totalAgams = statistics?.total_agam ?? 0;

  return (
    <AdminLayout>
      <div className="min-w-0 space-y-4 md:space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-foreground md:text-3xl">
              Export Agams Data
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Export Agams with custom filters and field selection
            </p>
          </div>
        </div>

        {/* Statistics Card */}
        {statistics && (
          <Card>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3 md:gap-4">
                <div className="flex items-center gap-3">
                  <FileTextIcon className="h-8 w-8 text-primary" />
                  <div>
                    <p className="text-sm text-muted-foreground">Total Agams</p>
                    <p className="text-2xl font-bold">
                      {totalAgams.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Export Filters
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="language">Language</Label>
                <Select
                  value={filters.language ?? "all"}
                  onValueChange={value =>
                    setFilters({
                      ...filters,
                      language: value === "all" ? undefined : value,
                    })
                  }
                >
                  <SelectTrigger id="language">
                    <SelectValue placeholder="All Languages" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Languages</SelectItem>
                    {languages.map(language => (
                      <SelectItem key={language.name} value={language.name}>
                        {language.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="search">Search</Label>
                <Input
                  id="search"
                  placeholder="Search in titles, keywords..."
                  value={filters.search ?? ""}
                  onChange={e =>
                    setFilters({
                      ...filters,
                      search: e.target.value || undefined,
                    })
                  }
                />
              </div>
            </div>
            <div className="flex justify-end pt-4">
              <Button
                onClick={handlePreview}
                disabled={previewLoading}
                variant="outline"
                className="flex items-center gap-2"
              >
                {previewLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading...
                  </>
                ) : (
                  <>
                    <Search className="h-4 w-4" />
                    Preview Results
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Preview Results Table */}
        {showPreview && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Eye className="h-5 w-5" />
                  Filtered Results Preview
                </span>
                <span className="text-sm font-normal text-muted-foreground">
                  {previewTotal.toLocaleString()} total Agam(s) found
                  {previewResults.length < previewTotal &&
                    ` (Showing first ${previewResults.length})`}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {previewResults.length === 0 ? (
                <div className="py-8 text-center text-muted-foreground">
                  No Agams found matching your filters
                </div>
              ) : (
                <div
                  className="w-full overflow-x-auto overflow-y-auto rounded-lg border"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "60vh",
                    WebkitOverflowScrolling: "touch",
                  }}
                >
                  <table
                    className="table-fixed border-collapse"
                    style={{
                      minWidth: "800px",
                      width: "100%",
                      tableLayout: "fixed",
                    }}
                  >
                    <thead className="bg-muted">
                      <tr>
                        <th className="h-12 w-24 whitespace-nowrap border-b px-4 text-left align-middle font-medium text-muted-foreground">
                          Serial No
                        </th>
                        <th className="h-12 min-w-[200px] border-b px-4 text-left align-middle font-medium text-muted-foreground">
                          Scripture Name
                        </th>
                        <th className="h-12 min-w-[200px] border-b px-4 text-left align-middle font-medium text-muted-foreground">
                          Mool Scripture
                        </th>
                        <th className="h-12 min-w-[150px] border-b px-4 text-left align-middle font-medium text-muted-foreground">
                          Author
                        </th>
                        <th className="h-12 w-24 whitespace-nowrap border-b px-4 text-left align-middle font-medium text-muted-foreground">
                          Category
                        </th>
                        <th className="h-12 w-24 whitespace-nowrap border-b px-4 text-left align-middle font-medium text-muted-foreground">
                          Language
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewResults.map((agam, index) => (
                        <tr
                          key={agam.all_serial ?? agam.id ?? index}
                          className="border-b transition-colors hover:bg-muted/50"
                        >
                          <td className="whitespace-nowrap p-4 align-middle font-mono text-xs">
                            {agam.all_serial ?? agam.id ?? "-"}
                          </td>
                          <td className="min-w-[200px] p-4 align-middle">
                            <div
                              className="max-w-[300px] truncate"
                              title={agam.scripture_name ?? "-"}
                            >
                              {agam.scripture_name ?? "-"}
                            </div>
                          </td>
                          <td className="min-w-[200px] p-4 align-middle">
                            <div
                              className="max-w-[300px] truncate"
                              title={
                                agam.mool_scripture_name ??
                                agam.trans_scripture_name ??
                                "-"
                              }
                            >
                              {agam.mool_scripture_name ??
                                agam.trans_scripture_name ??
                                "-"}
                            </div>
                          </td>
                          <td className="min-w-[150px] p-4 align-middle">
                            <div
                              className="max-w-[200px] truncate"
                              title={agam.author ?? agam.original_author ?? "-"}
                            >
                              {agam.author ?? agam.original_author ?? "-"}
                            </div>
                          </td>
                          <td className="whitespace-nowrap p-4 align-middle">
                            {agam.category ??
                              agam.category1 ??
                              agam.category2 ??
                              agam.category3 ??
                              "-"}
                          </td>
                          <td className="whitespace-nowrap p-4 align-middle">
                            {agam.mool_language ?? agam.trans_language ?? "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Custom Field Selection */}
        <Card>
          <CardHeader>
            <CardTitle>Select Fields to Export</CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              Choose which fields to include in the exported file
            </p>
          </CardHeader>
          <CardContent>
            <div className="mb-4 flex gap-2">
              <Button variant="outline" size="sm" onClick={selectAllFields}>
                Select All
              </Button>
              <Button variant="outline" size="sm" onClick={deselectAllFields}>
                Deselect All
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
              {AGAM_FIELDS.map(field => (
                <div key={field.key} className="flex items-center space-x-2">
                  <Checkbox
                    id={field.key}
                    checked={selectedFields.has(field.key)}
                    onCheckedChange={() => toggleField(field.key)}
                  />
                  <Label
                    htmlFor={field.key}
                    className="cursor-pointer text-sm font-normal"
                  >
                    {field.label}
                  </Label>
                </div>
              ))}
            </div>
            {selectedFields.size === 0 && (
              <p className="mt-4 text-sm text-muted-foreground">
                ⚠️ No fields selected. Default fields will be used.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Export Format Selection */}
        <Card>
          <CardHeader>
            <CardTitle>Export Format</CardTitle>
          </CardHeader>
          <CardContent className="min-w-0 overflow-x-hidden">
            <div className="grid w-full min-w-0 grid-cols-1 gap-3 xl:grid-cols-[repeat(3,minmax(0,1fr))]">
              <Button
                variant={exportFormat === "csv" ? "default" : "outline"}
                onClick={() => setExportFormat("csv")}
                className="flex min-h-10 w-full min-w-0 max-w-full items-center justify-center gap-2 whitespace-normal"
              >
                <FileText className="h-4 w-4 shrink-0" />
                CSV
              </Button>
              <Button
                variant={exportFormat === "excel" ? "default" : "outline"}
                onClick={() => setExportFormat("excel")}
                className="flex min-h-10 w-full min-w-0 max-w-full items-center justify-center gap-2 whitespace-normal"
              >
                <FileSpreadsheet className="h-4 w-4 shrink-0" />
                Excel
              </Button>
              <Button
                variant={exportFormat === "json" ? "default" : "outline"}
                onClick={() => setExportFormat("json")}
                className="flex min-h-10 w-full min-w-0 max-w-full items-center justify-center gap-2 whitespace-normal"
              >
                <FileJson className="h-4 w-4 shrink-0" />
                JSON
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Export Progress */}
        {exporting && (
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Status:</span>
                  <span className="font-medium">{exportProgress.status}</span>
                </div>
                {exportProgress.totalPages > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Progress:</span>
                    <span className="font-medium">
                      Page {exportProgress.currentPage} of{" "}
                      {exportProgress.totalPages}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">
                    Records Fetched:
                  </span>
                  <span className="font-medium">
                    {exportProgress.fetchedRecords.toLocaleString()}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Export Button */}
        <div className="flex justify-end">
          <Button
            onClick={handleExport}
            disabled={exporting || loading}
            size="lg"
            className="flex items-center gap-2"
          >
            {exporting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="h-4 w-4" />
                Export Agams
              </>
            )}
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
};

export default ExportAgam;
